/**
 * GÉNÉRATEUR DE REÇUS PDF - FOPAM
 * 
 * Format A5 Paysage (210 x 148 mm)
 * Format épuré et professionnel
 */

import { jsPDF } from 'jspdf';
import { amountToWords } from './numberToWords';

/**
 * Formater un montant avec XOF et séparateur de milliers (espace)
 */
const formatAmount = (amount) => {
  // Formatage manuel pour éviter les problèmes de caractères spéciaux
  const numStr = Math.floor(amount).toString();
  const parts = [];
  
  // Découper en groupes de 3 chiffres de droite à gauche
  for (let i = numStr.length; i > 0; i -= 3) {
    const start = Math.max(0, i - 3);
    parts.unshift(numStr.slice(start, i));
  }
  
  return `XOF ${parts.join(' ')}`;
};

/**
 * Formater une date en français
 */
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const day = String(d.getDate()).padStart(2, '0');
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  return `Abidjan, le ${day} ${month} ${year}`;
};

/**
 * Formater une période (mois année)
 */
const formatPeriod = (date) => {
  const d = new Date(date);
  const months = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
                  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
};

/**
 * Dessiner le logo FOPAM (image v2)
 */
const drawLogo = async (doc, x, y, size = 15) => {
  try {
    // Charger l'image du logo v2
    const logoPath = '/assets/logo-fopam-v2.png';
    const img = new Image();
    
    return new Promise((resolve, reject) => {
      img.onload = () => {
        doc.addImage(img, 'PNG', x, y, size, size);
        resolve();
      };
      img.onerror = (error) => {
        console.error('Erreur chargement logo:', error);
        // Fallback: dessiner un cercle simple
        doc.setFillColor(84, 106, 123);
        doc.circle(x + size/2, y + size/2, size/2, 'F');
        resolve();
      };
      img.src = logoPath;
    });
  } catch (error) {
    console.error('Erreur logo:', error);
    // Fallback: dessiner un cercle simple
    doc.setFillColor(84, 106, 123);
    doc.circle(x + size/2, y + size/2, size/2, 'F');
  }
};

/**
 * Génère un reçu FOPAM
 */
export const generateReceipt = async (payment, options = {}) => {
  const { isDuplicate = false, download = true } = options;

  try {
    // Format A5 Paysage (210 x 148 mm)
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a5'
    });

    const pageWidth = 210;
    const pageHeight = 148;
    const margin = 20;

    // ==================== FILIGRANE DUPLICATA ====================
    if (isDuplicate) {
      doc.saveGraphicsState();
      doc.setGState(new doc.GState({ opacity: 0.15 }));
      doc.setTextColor(220, 53, 69); // Rouge
      doc.setFontSize(60);
      doc.setFont('helvetica', 'bold');
      
      // Rotation inversée: bas gauche → haut droit (-45°)
      const centerX = pageWidth / 2;
      const centerY = pageHeight / 2;
      doc.text('DUPLICATA', centerX, centerY, {
        angle: -45,
        align: 'center'
      });
      
      doc.restoreGraphicsState();
    }

    let yPos = 12;

    // ==================== EN-TÊTE ====================
    // Logo FOPAM (gauche)
    const logoSize = 15;
    await drawLogo(doc, margin, yPos, logoSize);

    // Texte "FOPAM" à côté du logo - même hauteur que le logo
    // 15mm ≈ 42pt, donc police à 40pt pour correspondre visuellement
    doc.setFontSize(40);
    doc.setTextColor(84, 106, 123); // Bleu-gris #546A7B
    doc.setFont('helvetica', 'bold');
    doc.text('FOPAM', margin + logoSize + 5, yPos + logoSize/2 + 10);

    // Date centrée à droite (sans trait d'union)
    const dateText = formatDate(payment.paidAt || payment.createdAt);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(dateText, pageWidth - margin, yPos + logoSize/2 + 2, { align: 'right' });

    // Espacement significatif
    yPos += logoSize + 15;

    // Encadré montant (XOF au début)
    const boxWidth = 60;
    const boxHeight = 12;
    const boxX = pageWidth - margin - boxWidth;
    
    doc.setLineWidth(1.5);
    doc.setDrawColor(84, 106, 123); // Bordure bleu-gris
    doc.rect(boxX, yPos, boxWidth, boxHeight);
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(245, 166, 35); // Orange #F5A623
    doc.text(formatAmount(payment.amount), 
      boxX + boxWidth / 2, yPos + 8, { align: 'center' });

    yPos += 20;

    // ==================== LIGNE 1: REÇU N° et MATRICULE N° ====================
    doc.setFontSize(11);
    doc.setTextColor(84, 106, 123); // Bleu-gris
    doc.setFont('helvetica', 'bold');
    doc.text('Reçu N° :', margin, yPos);
    
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(payment.receiptNumber, margin + 22, yPos);

    // Matricule (droite)
    const matricule = payment.metadata?.mutualisteMatricule || 
                     payment.mutualiste?.matricule || 'N/A';
    doc.setTextColor(84, 106, 123); // Bleu-gris
    doc.setFont('helvetica', 'bold');
    doc.text('Matricule N° :', pageWidth / 2 + 10, yPos);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(matricule, pageWidth / 2 + 40, yPos);

    yPos += 8;

    // ==================== LIGNE 2: DÉLIVRÉ À ====================
    const beneficiaire = payment.metadata?.mutualisteName || 
                        (payment.mutualiste ? 
                          `${payment.mutualiste.prenoms} ${payment.mutualiste.nom}` : 'N/A');
    
    doc.setTextColor(84, 106, 123);
    doc.setFont('helvetica', 'bold');
    doc.text('Délivré à :', margin, yPos);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(beneficiaire.toUpperCase(), margin + 25, yPos);

    yPos += 8;

    // ==================== LIGNE 3: POUR LA SOMME DE ====================
    doc.setTextColor(84, 106, 123);
    doc.setFont('helvetica', 'bold');
    doc.text('Pour la somme de :', margin, yPos);
    
    const amountInWords = amountToWords(Math.floor(payment.amount));
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(`${amountInWords} francs CFA`.toUpperCase(), margin + 43, yPos);

    yPos += 8;

    // ==================== LIGNE 4: EN RÈGLEMENT DES COTISATIONS DE ====================
    const produit = payment.metadata?.offerName || 'N/A';
    
    doc.setTextColor(84, 106, 123);
    doc.setFont('helvetica', 'bold');
    doc.text('En règlement des cotisations de :', margin, yPos);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(produit, margin + 68, yPos);

    yPos += 8;

    // ==================== LIGNE 5: OBJET DU PAIEMENT ====================
    doc.setTextColor(84, 106, 123);
    doc.setFont('helvetica', 'bold');
    doc.text('Objet du paiement :', margin, yPos);
    
    // Lister les périodes des contributions
    const contributions = payment.contributions || [];
    let periods = [];
    if (contributions.length > 0) {
      periods = contributions.map(pc => {
        const contrib = pc.contribution || pc;
        return formatPeriod(contrib.dueDate);
      });
    }
    const periodsText = periods.join(', ') || 'N/A';
    
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(periodsText, margin + 48, yPos);

    yPos += 15;

    // ==================== VISA DE LA CAISSE (DROITE - CENTRÉ) ====================
    const signatureWidth = 70;
    const signatureHeight = 35;
    const signatureX = pageWidth - margin - signatureWidth;
    const signatureY = yPos;
    
    // Zone de signature
    doc.setDrawColor(84, 106, 123); // Bordure bleu-gris
    doc.setLineWidth(1);
    doc.rect(signatureX, signatureY, signatureWidth, signatureHeight);
    
    // Texte "Visa de la caisse" CENTRÉ au-dessus du carré
    doc.setFontSize(10);
    doc.setTextColor(84, 106, 123);
    doc.setFont('helvetica', 'bold');
    doc.text('Visa de la caisse', signatureX + signatureWidth / 2, signatureY - 3, { align: 'center' });

    // Cachet "PAYÉ" en rouge et grand
    doc.setFontSize(16);
    doc.setTextColor(220, 53, 69); // Rouge
    doc.setFont('helvetica', 'bold');
    doc.text('PAYÉ', signatureX + signatureWidth / 2, signatureY + 8, { align: 'center' });

    // Récupérer l'utilisateur connecté
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    let userName = 'Système';
    if (currentUser.role === 'MUTUALISTE') {
      userName = 'Système';
    } else if (currentUser.firstName && currentUser.lastName) {
      userName = `${currentUser.firstName} ${currentUser.lastName}`;
    } else if (currentUser.email) {
      userName = currentUser.email.split('@')[0];
    }

    // Nom de l'utilisateur
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.text(userName, signatureX + signatureWidth / 2, signatureY + 18, { align: 'center' });

    // Date et heure d'édition
    const now = new Date();
    const dateTime = `${now.toLocaleDateString('fr-FR')} ${now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text(dateTime, signatureX + signatureWidth / 2, signatureY + 23, { align: 'center' });
    
    // Ligne de séparation
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.3);
    doc.line(signatureX + 5, signatureY + 25, signatureX + signatureWidth - 5, signatureY + 25);

    // ==================== TÉLÉCHARGEMENT ====================
    if (download) {
      const filename = isDuplicate 
        ? `duplicata-${payment.receiptNumber}.pdf`
        : `recu-${payment.receiptNumber}.pdf`;
      doc.save(filename);
    }

    return doc;

  } catch (error) {
    console.error('Erreur génération reçu:', error);
    throw error;
  }
};

/**
 * Génère un reçu normal (sans filigrane)
 */
export const generateNormalReceipt = async (payment) => {
  return await generateReceipt(payment, { isDuplicate: false, download: true });
};

/**
 * Génère un duplicata de reçu (avec filigrane DUPLICATA)
 */
export const generateDuplicateReceipt = async (payment) => {
  return await generateReceipt(payment, { isDuplicate: true, download: true });
};
